package com.pincer.app.viewmodel

import com.pincer.app.ApplicationState
import com.pincer.app.I18n
import com.pincer.core.model.tree.PincerType
import com.pincer.core.model.tree.PincerType.STANDARD
import com.pincer.core.model.tree.PincerType.TEMPLATE
import com.pincer.core.model.tree.Answer
import com.pincer.core.model.tree.Participant
import com.pincer.core.model.tree.QuestionFormat
import com.pincer.core.model.tree.LIFECYCLE_SCOPE
import com.pincer.core.model.tree.CHATS_SCOPE
import com.pincer.core.model.tree.PERMISSIONS_SCOPE
import com.pincer.core.model.tree.QUESTIONS_SCOPE
import com.pincer.core.model.tree.CANDIDATES_SCOPE
import com.pincer.core.model.tree.NAME_SCOPE
import com.pincer.core.model.tree.DESCRIPTION_SCOPE
import com.pincer.core.model.tree.RULES_SCOPE
import com.pincer.core.model.tree.PARTICIPANTS_SCOPE
import com.pincer.core.model.tree.PARTICIPANTS_ANSWERS_SCOPE
import com.pincer.core.model.tree.PARTICIPANTS_RULES_SCOPE
import com.pincer.core.model.tree.PARTICIPANTS_ROLES_SCOPE
import com.pincer.core.model.drafts.QuestionDraft
import com.pincer.core.model.drafts.AnswerDraft
import com.pincer.core.functions.termsNeeded
import com.pincer.core.functions.getAnswer
import com.pincer.core.functions.readable
import com.pincer.core.functions.readablePid
import com.pincer.core.functions.getRolesForParticipant
import com.pincer.core.functions.validateText
import com.pincer.core.functions.validateEmail
import com.pincer.core.functions.validateCode
import com.pincer.core.functions.validateQuestionCode
import com.pincer.core.functions.validateRegex
import com.pincer.core.functions.validateRoles
import com.pincer.core.functions.validateRole
import com.pincer.core.functions.validateOptions
import com.pincer.core.functions.validateAnswer
import com.pincer.core.functions.validateRules

fun pincerSettings(state: ApplicationState, context: Node) {
    val localPincer = state.pincer()!!
    val uiPincer = state.pincerUi()!!
    val selfParticipant: Participant? = state.pincerSelf()
    val hasNameScope = state.checkWrite(NAME_SCOPE)
    val hasDescriptionScope = state.checkWrite(DESCRIPTION_SCOPE)
    val hasParticipantsRolesScope = state.checkWrite(PARTICIPANTS_ROLES_SCOPE)
    val hasRulesScope = state.checkWrite(RULES_SCOPE)
    val hasQuestionsScope = state.checkWrite(QUESTIONS_SCOPE)
    val hasLifecycleScope = state.checkWrite(LIFECYCLE_SCOPE)
    val showAdvancedOptions = state.stored.showAdvancedOptions == "true"
    //if (!(hasNameScope || hasDescriptionScope || hasParticipantsRolesScope || hasRulesScope || hasQuestionsScope || hasLifecycleScope || showAdvancedOptions)) return
    context.opening(state = state, key = "settings", wording = I18n.translate("pincerSettings"), atFirst = false, prominence = Prominence.Secondary) {
        node("pid") {
            node("value", readablePid(state.pincer()!!))
        }
        node("shareableLink") {
            node("value", "https://web.pincer.com/#${localPincer.pid}")
        }
        if (hasNameScope) {
            val textInBox = uiPincer.name ?: localPincer.name
            val problem = validateText(textInBox)
            field(state = state, key = "pincerName", value = localPincer.name, problem = problem) {
                input(key = "setPincerName", action = Action.SetPincerName, wording = textInBox ?: "", problem = problem)
                possibleProblem(problem)
            }
        }
        if (hasDescriptionScope) {
            val textInBox = uiPincer.description ?: localPincer.description
            val problem = validateText(textInBox)
            field(state = state, key = "pincerDescription", value = localPincer.description, problem = problem) {
                input(key = "setPincerDescription", action = Action.SetPincerDescription, wording = textInBox ?: "", problem = problem)
                possibleProblem(problem)
            }
        }
        // No scope check here. Participants can all see each other?
        pincerParticipants(state, this)

        if (hasParticipantsRolesScope) {
            if (localPincer.openRole == null || localPincer.openRole == "attendee") {
                node("useOpenRole") {
                    val w = if (localPincer.openRole == null) "false" else "true"
                    checkbox(key = "toggleUseOpenRole", action = Action.SetUseOpenRole, wording = w)
                    helpText("useOpenRoleHelpText")
                }
            }
            if (showAdvancedOptions) {
                val textInBox = uiPincer.openRole ?: localPincer.openRole
                val problem = validateRole(textInBox)
                field(state = state, key = "openRole", value = localPincer.openRole, problem = problem) {
                    input(key = "setOpenRole", action = Action.SetOpenRole, wording = textInBox ?: "", problem = problem)
                    possibleProblem(problem)
                    helpText("openRoleHelpText")
                }
            }
        }
        if (showAdvancedOptions && hasRulesScope) {
            val textInBox = uiPincer.rules ?: localPincer.rules
            val problem = validateRules(textInBox)
            field(state = state, key = "pincerRules", value = localPincer.rules, problem = problem) {
                input(key = "setPincerRules", action = Action.SetPincerRules, wording = textInBox, problem = problem)
                possibleProblem(problem)
            }
        }
        if (hasQuestionsScope) {
            val count = localPincer.questions.size
            opening(state = state, key = "questions", atFirst = false, summary = I18n.translate(if (count == 1) "questions-summary-one" else "questions-summary", count.toString())) {
                for (questionId in localPincer.questions.keys) {
                    val question = localPincer.questions[questionId]!!
                    if (uiPincer.questions.get(questionId) == null) {
                        uiPincer.questions.set(questionId, QuestionDraft())
                    }
                    val uiQuestion = uiPincer.questions.get(questionId)!!
                    node(questionId, question.code) {
                        val codeInBox = uiQuestion.code ?: question.code
                        val codeProblem = validateQuestionCode(codeInBox)
                        field(state = state, key = "code", wording = I18n.translate("question-code"), value = question.code, problem = codeProblem) {
                            input(key = "setQuestionCode", action = Action.SetQuestionCode(questionId), wording = codeInBox, problem = codeProblem)
                            possibleProblem(codeProblem)
                            helpText("question-code-help")
                        }
                        val wordingInBox = uiQuestion.wording ?: question.wording
                        val wordingProblem = validateText(wordingInBox)
                        field(state = state, key = "wording", wording = I18n.translate("question-wording"), value = question.wording, problem = wordingProblem) {
                            input(key = "setQuestionWording", action = Action.SetQuestionWording(questionId), wording = wordingInBox, problem = wordingProblem)
                            possibleProblem(wordingProblem)
                        }
                        node("format", I18n.translate("question-format")) {
                            val controlChoices = mutableMapOf<String, String>()
                            QuestionFormat.values().forEach {
                                if (it == QuestionFormat.TIMESTAMP && question.format != "TIMESTAMP") {
                                    // skip
                                }
                                else {
                                    controlChoices.put(it.toString(), it.toString())
                                }
                            }
                            select(key = "setQuestionFormat", action = Action.SetQuestionFormat(questionId), wording = question.format, controlChoices = controlChoices)
                            // no invalidation check here ... we just have a gray box
                        }
                        val helpTextInBox = uiQuestion.helpText ?: question.helpText
                        val helpTextProblem = validateText(helpTextInBox)
                        field(state = state, key = "helpText", wording = I18n.translate("question-help-text"), value = question.helpText, problem = helpTextProblem) {
                            input(key = "setQuestionHelpText", action = Action.SetQuestionHelpText(questionId), wording = helpTextInBox ?: "", problem = helpTextProblem)
                            possibleProblem(helpTextProblem)
                        }
                        if (question.format == QuestionFormat.SELECT.toString()) {
                            val textInBox = uiQuestion.options ?: question.options
                            val problem = validateOptions(textInBox)
                            field(state = state, key = "options", wording = I18n.translate("question-options"), value = question.options, problem = problem) {
                                multiline(key = "setQuestionOptions", action = Action.SetQuestionOptions(questionId), wording = textInBox ?: "", problem = problem)
                                possibleProblem(problem)
                                helpText("question-options-help")
                            }
                        }
                        if (question.format == QuestionFormat.REGEX.toString()) {
                            val textInBox = uiQuestion.regex ?: question.regex
                            val problem = validateRegex(textInBox)
                            field(state = state, key = "regex", wording = I18n.translate("question-regex"), value = question.regex, problem = problem) {
                                input(key = "setQuestionRegex", action = Action.SetQuestionRegex(questionId), wording = textInBox ?: "", problem = problem)
                                possibleProblem(problem)
                                helpText("question-regex-help")
                            }
                        }
                        val rolesInBox = uiQuestion.roles ?: question.roles
                        val rolesProblem = validateRoles(rolesInBox)
                        field(state = state, key = "roles", wording = I18n.translate("question-roles"), value = question.roles, problem = rolesProblem) {
                            input(key = "setQuestionRoles", action = Action.SetQuestionRoles(questionId), wording = rolesInBox ?: "", problem = rolesProblem)
                            possibleProblem(rolesProblem)
                            helpText("question-roles-help")
                        }
                    }
                }
                node("newQuestion") {
                    node("newQuestionCode", I18n.translate("question-code")) {
                        val problem = validateQuestionCode(state.transient.newQuestion.code)
                        input(key = "setNewQuestionCode", action = Action.SetNewQuestionCode, wording = state.transient.newQuestion.code ?: "", problem = problem)
                        possibleProblem(problem)
                        helpText("question-code-help")
                    }
                    node("newQuestionWording", I18n.translate("question-wording")) {
                        val problem = validateText(state.transient.newQuestion.wording)
                        input(key = "setNewQuestionWording", action = Action.SetNewQuestionWording, wording = state.transient.newQuestion.wording ?: "", problem = problem)
                        possibleProblem(problem)
                    }
                    node("newQuestionFormat", I18n.translate("question-format")) {
                        // todo share with earlier? static on QuestionFormat?
                        val controlChoices = mutableMapOf<String, String>()
                        QuestionFormat.values().forEach {
                            if (it == QuestionFormat.TIMESTAMP) {
                                // skip
                            }
                            else {
                                controlChoices.put(it.toString(), it.toString())
                            }
                        }
                        select(key = "setNewQuestionFormat", action = Action.SetNewQuestionFormat, wording = state.transient.newQuestion.format ?: "SIMPLE", controlChoices = controlChoices)
                    }
                    if (state.transient.newQuestion.format == QuestionFormat.SELECT.toString()) {
                        node("newQuestionOptions", I18n.translate("question-options")) {
                            val problem = validateOptions(state.transient.newQuestion.options)
                            multiline(key = "setNewQuestionOptions", action = Action.SetNewQuestionOptions, wording = state.transient.newQuestion.options ?: "", problem = problem)
                            possibleProblem(problem)
                            helpText("question-options-help")
                        }
                    }
                    if (state.transient.newQuestion.format == QuestionFormat.REGEX.toString()) {
                        node("newQuestionRegex", I18n.translate("question-regex")) {
                            val problem = validateRegex(state.transient.newQuestion.regex)
                            input(key = "setNewQuestionRegex", action = Action.SetNewQuestionRegex, wording = state.transient.newQuestion.regex ?: "", problem = problem)
                            possibleProblem(problem)
                            helpText("question-regex-help")
                        }
                    }
                    node("newQuestionHelpText", I18n.translate("question-help-text")) {
                        val problem = validateText(state.transient.newQuestion.helpText)
                        input(key = "setNewQuestionHelpText", action = Action.SetNewQuestionHelpText, wording = state.transient.newQuestion.helpText ?: "", problem = problem)
                        possibleProblem(problem)
                    }
                    node("newQuestionRoles", I18n.translate("question-roles")) {
                        val problem = validateRoles(state.transient.newQuestion.roles)
                        input(key = "setNewQuestionRoles", action = Action.SetNewQuestionRoles, wording = state.transient.newQuestion.roles ?: "", problem = problem)
                        possibleProblem(problem)
                        helpText("question-roles-help")
                    }
                    button(key = "addQuestion", action = Action.AddQuestion)
                }
            }
        }
        if (localPincer.pincerType == PincerType.STANDARD && hasLifecycleScope) {
            node("makeTemplate") {
                button(key = "makeTemplate", action = Action.MakeTemplate)
                helpText("makeTemplateHelpText")
            }
        }
        if (showAdvancedOptions && selfParticipant != null) {
            val redInBox = state.pincerUiSelf()!!.redRules ?: selfParticipant.redRules
            val redProblem = validateRules(redInBox)
            field(state = state, key = "redRules", value = selfParticipant.redRules, problem = redProblem) {
                input(key = "setRedRules", action = Action.SetRedRules, wording = redInBox, problem = redProblem)
                possibleProblem(redProblem)
            }
            val greenInBox = state.pincerUiSelf()!!.greenRules ?: selfParticipant.greenRules
            val greenProblem = validateRules(greenInBox)
            field(state = state, key = "greenRules", value = selfParticipant.greenRules, problem = greenProblem) {
                input(key = "setGreenRules", action = Action.SetGreenRules, wording = greenInBox, problem = greenProblem)
                possibleProblem(greenProblem)
            }
            node("stencil") {
                checkbox(key = "toggleStencil", action = Action.SetStencil, wording = selfParticipant.stencil.toString())
            }
        }
    }
}